import { supplierNavConfig } from "@/config/navigation/supplierNav";
import { cn } from "@/lib/utils";
import { NavSection } from "@/components/navigation/NavSection";
import { NavItem } from "@/components/navigation/NavItem";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { useSidebar } from "@/contexts/SidebarContext";
import { ChevronLeft, ChevronRight, Settings } from "lucide-react";

interface SupplierSidebarProps {
  variant?: "desktop" | "drawer";
  onNavigate?: () => void;
}

export function SupplierSidebar({
  variant = "desktop",
  onNavigate,
}: SupplierSidebarProps) {
  const navigate = useNavigate();
  const { sections } = supplierNavConfig;
  const [companyName, setCompanyName] = useState("Supplier workspace");
  const { isCollapsed, toggleSidebar } = useSidebar();

  useEffect(() => {
    let isMounted = true;
    const loadProfile = async () => {
      const { data } = await supabase.auth.getUser();
      const userId = data.user?.id;
      if (!userId) return;
      const { data: profile } = await supabase
        .from("profiles")
        .select("company_name")
        .eq("id", userId)
        .maybeSingle();
      if (profile?.company_name && isMounted) {
        setCompanyName(profile.company_name);
      }
    };
    loadProfile();
    return () => {
      isMounted = false;
    };
  }, []);

  const handleLogoClick = () => {
    navigate("/supplier/dashboard");
    if (onNavigate) {
      onNavigate();
    }
  };

  const containerClasses = "flex h-full flex-col bg-white border-r border-border w-full overflow-hidden";

  return (
    <div className={containerClasses}>
      <div
        className={cn(
          "flex items-center justify-between border-b border-border transition-all overflow-hidden",
          isCollapsed ? "px-0 py-3" : "px-6 py-5"
        )}
      >
        {!isCollapsed ? (
          <>
            <div 
              className="flex min-w-0 flex-1 cursor-pointer flex-col items-start transition-opacity hover:opacity-80"
              onClick={handleLogoClick}
              title="Go to Dashboard"
            >
              <div className="flex items-center gap-4">
                <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">
                  HS
                </div>
                <h1 className="text-xl font-bold uppercase tracking-tight text-foreground">HoleScale.</h1>
              </div>
              <p className="mt-1 truncate text-xs text-muted-foreground" title={companyName}>{companyName}</p>
            </div>
            <button
              onClick={toggleSidebar}
              className="shrink-0 rounded-xl p-2 transition-colors hover:bg-muted"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft className="size-5 text-muted-foreground" />
            </button>
          </>
        ) : (
          <div className="flex w-full flex-col items-center gap-4">
            <button
              onClick={toggleSidebar}
              className="flex w-full justify-center rounded-xl p-2 transition-colors hover:bg-muted"
              aria-label="Expand sidebar"
            >
              <ChevronRight className="size-5 text-muted-foreground" />
            </button>
            <div 
              className="flex size-10 cursor-pointer items-center justify-center transition-opacity hover:opacity-80"
              onClick={handleLogoClick}
              title="Go to Dashboard"
            >
              <div className="flex size-8 items-center justify-center rounded-lg bg-primary text-sm font-bold text-white">
                HS
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={cn(
        "flex-1 overflow-y-auto overflow-x-hidden transition-all sidebar-scroll",
        isCollapsed ? "py-2 px-3 pb-2" : "py-4 px-5 pb-8 space-y-1.5"
      )}>
        {!isCollapsed ? (
          sections.map((section) => (
            <NavSection
              key={section.id}
              section={section}
              onNavigate={onNavigate}
              collapsed={false}
            />
          ))
        ) : (
          <div className="flex w-full flex-col items-center gap-2">
            {sections.flatMap((section) => {
              return section.items.map((item) => (
                <NavItem
                  key={item.href}
                  item={item}
                  onNavigate={onNavigate}
                  collapsed={true}
                  isInQuickActions={section.id === "quick-actions"}
                />
              ));
            })}
          </div>
        )}
      </div>

      <div className={cn("border-t border-border flex-shrink-0", isCollapsed ? "p-2 flex justify-center" : "py-4 px-4")}>
        <a
          href="/supplier/settings"
          onClick={(e) => {
            e.preventDefault();
            navigate("/supplier/settings");
            onNavigate?.();
          }}
          className={cn(
            "flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2",
            isCollapsed ? "w-11 h-11 p-0" : "gap-2 w-full justify-start p-2"
          )}
          aria-label="Settings"
        >
          <Settings className="size-5 shrink-0" />
          {!isCollapsed && <span className="text-sm font-medium">Settings</span>}
        </a>
      </div>
    </div>
  );
}
